import React, { useState, useEffect } from 'react';
import DualRangeSlider from './DualRangeSlider';
import './Filtros.css';

const Filtros = ({ onFilterChange, filters, onSearch }) => {
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Função para obter limites de área baseado no tipo de imóvel
  const getAreaLimits = (tipo) => {
    if (tipo === 'Chácara') {
      return { min: 1000, max: 10000, step: 50 };
    } else {
      // Casa, Casa Geminada, Apartamento, Cobertura, Duplex, Triplex ou sem seleção
      return { min: 20, max: 500, step: 10 };
    }
  };

  // Ajustar valores de área quando o tipo mudar
  useEffect(() => {
    const areaLimits = getAreaLimits(filters.tipo);
    const currentAreaMin = parseInt(filters.areaMin || areaLimits.min);
    const currentAreaMax = parseInt(filters.areaMax || areaLimits.max);

    // Se os valores atuais estão fora dos novos limites, ajustar
    if (currentAreaMin < areaLimits.min || currentAreaMin > areaLimits.max ||
        currentAreaMax < areaLimits.min || currentAreaMax > areaLimits.max ||
        currentAreaMin > currentAreaMax) {
      const newFilters = {
        ...filters,
        areaMin: areaLimits.min,
        areaMax: areaLimits.max
      };
      onFilterChange(newFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.tipo]);

  const handleChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    
    // Se mudou o tipo, ajustar limites de área
    if (key === 'tipo') {
      const areaLimits = getAreaLimits(value);
      const currentAreaMin = parseInt(newFilters.areaMin || areaLimits.min);
      const currentAreaMax = parseInt(newFilters.areaMax || areaLimits.max);
      
      // Ajustar valores se estiverem fora dos limites
      if (currentAreaMin < areaLimits.min || currentAreaMin > areaLimits.max) {
        newFilters.areaMin = areaLimits.min;
      }
      if (currentAreaMax < areaLimits.min || currentAreaMax > areaLimits.max) {
        newFilters.areaMax = areaLimits.max;
      }
      if (currentAreaMin > currentAreaMax) {
        newFilters.areaMin = areaLimits.min;
        newFilters.areaMax = areaLimits.max;
      }
    }
    
    onFilterChange(newFilters);
  };

  const handleRangeChange = (type, range) => {
    if (type === 'preco') {
      onFilterChange({
        ...filters,
        precoMin: range.min,
        precoMax: range.max
      });
    } else if (type === 'area') {
      onFilterChange({
        ...filters,
        areaMin: range.min,
        areaMax: range.max
      });
    }
  };

  const formatCurrency = (value) => {
    if (!value) return '';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0
    }).format(value);
  };

  return (
    <div className="filtros-container">
      <div className="filtros-basicos">
        <select
          value={filters.cidade || ''}
          onChange={(e) => handleChange('cidade', e.target.value)}
          className="filtro-select"
        >
          <option value="">Todas as cidades</option>
          <option value="Belo Horizonte">Belo Horizonte</option>
          <option value="Santa Luzia">Santa Luzia</option>
          <option value="São José da Lapa">São José da Lapa</option>
          <option value="Vespasiano">Vespasiano</option>
        </select>

        <select
          value={filters.tipo || ''}
          onChange={(e) => handleChange('tipo', e.target.value)}
          className="filtro-select"
        >
          <option value="">Todos os tipos</option>
          <option value="Apartamento">Apartamento</option>
          <option value="Casa">Casa</option>
          <option value="Casa Geminada">Casa Geminada</option>
          <option value="Chácara">Chácara</option>
          <option value="Cobertura">Cobertura</option>
          <option value="Lote">Lote</option>
          <option value="Sitio">Sitio</option>
        </select>

        <input
          type="text"
          placeholder="Bairro"
          value={filters.bairro || ''}
          onChange={(e) => handleChange('bairro', e.target.value)}
          className="filtro-input"
        />

        <button
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="btn-toggle-filtros"
        >
          {showAdvanced ? 'Menos filtros' : 'Mais filtros'}
        </button>
      </div>

      {showAdvanced && (
        <div className="filtros-avancados">
          <div className="filtro-group">
            <label>Quartos</label>
            <select
              value={filters.quartos || ''}
              onChange={(e) => handleChange('quartos', e.target.value)}
              className="filtro-select"
            >
              <option value="">Qualquer</option>
              <option value="1">1</option>
              <option value="2">2</option>
              <option value="3">3</option>
              <option value="4">4</option>
              <option value="5">Mais que 4</option>
            </select>
          </div>

          <div className="filtro-group">
            <label>Banheiros</label>
            <select
              value={filters.banheiros || ''}
              onChange={(e) => handleChange('banheiros', e.target.value)}
              className="filtro-select"
            >
              <option value="">Qualquer</option>
              <option value="1">1</option>
              <option value="2">2</option>
              <option value="3">3</option>
              <option value="4">4</option>
              <option value="5">Mais que 4</option>
            </select>
          </div>

          <div className="filtro-group">
            <label>Vagas</label>
            <select
              value={filters.vagas || ''}
              onChange={(e) => handleChange('vagas', e.target.value)}
              className="filtro-select"
            >
              <option value="">Qualquer</option>
              <option value="1">1</option>
              <option value="2">2</option>
              <option value="3">3</option>
              <option value="4">4</option>
              <option value="5">Mais que 4</option>
            </select>
          </div>

          <div className="filtro-group filtro-range-group">
            <label>Preço</label>
            <DualRangeSlider
              min={50000}
              max={4000000}
              step={50000}
              valueMin={parseInt(filters.precoMin || 50000)}
              valueMax={parseInt(filters.precoMax || 4000000)}
              onChange={(range) => handleRangeChange('preco', range)}
              formatValue={formatCurrency}
            />
          </div>

          <div className="filtro-group filtro-range-group">
            <label>Área (m²)</label>
            {(() => {
              const areaLimits = getAreaLimits(filters.tipo);
              return (
                <DualRangeSlider
                  min={areaLimits.min}
                  max={areaLimits.max}
                  step={areaLimits.step}
                  valueMin={parseInt(filters.areaMin || areaLimits.min)}
                  valueMax={parseInt(filters.areaMax || areaLimits.max)}
                  onChange={(range) => handleRangeChange('area', range)}
                  formatValue={(value) => `${value} m²`}
                />
              );
            })()}
          </div>

          <div className="filtro-checkboxes">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={filters.novo === 'true'}
                onChange={(e) => handleChange('novo', e.target.checked ? 'true' : '')}
              />
              Novo
            </label>

            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={filters.usado === 'true'}
                onChange={(e) => handleChange('usado', e.target.checked ? 'true' : '')}
              />
              Usado
            </label>
          </div>
        </div>
      )}

      <div className="filtros-actions">
        <button onClick={onSearch} className="btn-pesquisar">
          Pesquisar
        </button>
      </div>
    </div>
  );
};

export default Filtros;

